import React, { useState, useEffect } from 'react';
import CalendarDisplay from './CalendarDisplay';
import AdminPanel from './AdminPanel';
import Login from './Login';
import '../styles/App.css';

function CalendarPage() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [darkMode, setDarkMode] = useState(false);
    const [rotationInterval, setRotationInterval] = useState(20);
    const [userRole, setUserRole] = useState(null); // 'admin' | 'readonly' | null
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    // Default to showing login if no valid session
    const [showLogin, setShowLogin] = useState(true);

    // In production, relative path works fine.
    const API_URL = '';

    // Load settings and events
    useEffect(() => {
        // Check for existing session
        const token = localStorage.getItem('authToken');
        if (token) {
            verifyToken(token);
        } else {
            setLoading(false); // No token, ready to show login
        }
    }, []);

    const verifyToken = (token) => {
        fetch(`${API_URL}/api/auth/verify`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    if (data.role) setUserRole(data.role);
                    else setUserRole('admin'); // Fallback

                    setShowLogin(false);
                    loadSettings();
                    loadEvents();

                    // Refresh events every 5 minutes
                    const interval = setInterval(loadEvents, 5 * 60 * 1000);
                    return () => clearInterval(interval);
                } else {
                    localStorage.removeItem('authToken');
                    setShowLogin(true);
                    setLoading(false);
                }
            })
            .catch(() => {
                localStorage.removeItem('authToken');
                setShowLogin(true);
                setLoading(false);
            });
    };

    const loadSettings = async () => {
        try {
            const response = await fetch(`${API_URL}/api/settings`);
            const data = await response.json();

            if (data.success) {
                setDarkMode(data.settings.darkMode === 'true');
                setRotationInterval(parseInt(data.settings.rotationInterval) || 20);
            }
        } catch (err) {
            console.error('Failed to load settings:', err);
        }
    };

    const loadEvents = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/api/events`, { headers });

            if (response.status === 401 || response.status === 403) {
                console.error('Unauthorized access to events');
                return;
            }

            const data = await response.json();

            if (data.success) {
                setEvents(data.events);
            }
        } catch (err) {
            console.error('Failed to load events:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = (data) => {
        // data contains { success: true, token, role }
        localStorage.setItem('authToken', data.token);
        setUserRole(data.role);
        setShowLogin(false);
        loadSettings();
        loadEvents();
    };

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        setUserRole(null);
        setShowAdminPanel(false);
        setShowLogin(true);
        // Note: We stay on this page, but show login screen
    };

    if (loading) {
        return <div className="loading-screen">Loading...</div>;
    }

    return (
        <div className={`app ${darkMode ? 'dark' : ''}`}>
            {!showLogin && !showAdminPanel && (
                <>
                    <header className={`header ${darkMode ? 'dark' : ''}`}>
                        <h1>LOS Calendar System</h1>
                        <div className="header-controls">
                            <button onClick={() => loadEvents()} className="btn btn-secondary">
                                Refresh
                            </button>
                            {userRole === 'admin' && (
                                <button onClick={() => setShowAdminPanel(true)} className="btn btn-primary">
                                    Admin
                                </button>
                            )}
                            <button onClick={handleLogout} className="btn btn-danger">
                                Logout
                            </button>
                        </div>
                    </header>

                    {events.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem' }}>
                            <h2>No events loaded</h2>
                            <p style={{ marginTop: '1rem', color: '#6b7280' }}>
                                {userRole === 'admin'
                                    ? 'Please upload a CSV file from the admin panel.'
                                    : 'No calendar data available.'}
                            </p>
                            {userRole === 'admin' && (
                                <button
                                    onClick={() => setShowAdminPanel(true)}
                                    className="btn btn-primary"
                                    style={{ marginTop: '1rem' }}
                                >
                                    Go to Admin Panel
                                </button>
                            )}
                        </div>
                    ) : (
                        <CalendarDisplay
                            events={events}
                            darkMode={darkMode}
                            rotationInterval={rotationInterval}
                        />
                    )}
                </>
            )}

            {showLogin && (
                <>
                    <header className={`header ${darkMode ? 'dark' : ''}`}>
                        <h1>LOS Calendar System</h1>
                        <div className="header-controls">
                            {/* Optional: Add a "Back to Home" button here if desired */}
                        </div>
                    </header>
                    <Login onLogin={handleLogin} />
                </>
            )}

            {showAdminPanel && (
                <>
                    <header className={`header ${darkMode ? 'dark' : ''}`}>
                        <h1>LOS Calendar System - Admin</h1>
                    </header>
                    <AdminPanel
                        onLogout={() => {
                            handleLogout();
                        }}
                        onBack={() => setShowAdminPanel(false)}
                        darkMode={darkMode}
                        setDarkMode={setDarkMode}
                        rotationInterval={rotationInterval}
                        setRotationInterval={setRotationInterval}
                    />
                </>
            )}
        </div>
    );
}

export default CalendarPage;
